import React, { useState, useRef, useEffect } from "react";
import {
  Typography,
  Grid,
  Box,
  Button,
  Tabs,
  Tab,
  Container,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { Links, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AdaptiveContainer, Space } from "../components/common";
import PageTitle from "../components/common/PageTitle";
import {
  NavigateNext,
  CheckCircle,
  FiberManualRecord,
  Info,
  Schedule,
  AttachMoney,
  Security,
  Language,
  Business,
  TravelExplore,
  Assignment,
  Email,
  CreditCard,
} from "@mui/icons-material";
import InfoStatCard from "../components/common/InfoStatCard";
import theme from "../theme/theme.js";
import InfoListCard from "../components/common/InfoListCard";
import NumberedList from "../components/common/NumberedList";
import YoutubeCard from "../components/common/YoutubeCard";
import { useTheme } from "@mui/material/styles";

const LearnAbout = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const tabsRef = useRef(null);

  useEffect(() => {
    // Scroll to tabs section when page loads with automatic header height measurement
    if (tabsRef.current) {
      // Find the sticky header section
      const stickyHeader = document.querySelector('[data-sticky-header]');
      const headerHeight = stickyHeader ? stickyHeader.offsetHeight : 0;
      
      const elementTop = tabsRef.current.offsetTop;
      const padding = 120; // Add some padding above the tabs
      window.scrollTo({
        top: elementTop - headerHeight - padding,
        behavior: "smooth"
      });
    }
  }, []); // Only run on mount

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const TabPanel = ({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            p: { xs: 3, md: 6 },
            backgroundColor: "white",
            borderRadius: "0px 8px 8px 8px",
            mt: 0,
            mb: 2,
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );

  const prefix = process.env.PUBLIC_URL || '';

  return (
    <>
      <PageTitle pageKey="learnAbout" titleKey="pageTitles.learnAbout" fallbackTitle={t("pageTitles.learnAbout")} />
      {/* Section sticky : Breadcrumbs + Header */}
      <Box
        data-sticky-header
        sx={{
          position: isMobile ? "static" : "sticky",
          top: isMobile ? undefined : 80,
          zIndex: isMobile ? undefined : 100,
          backgroundColor: "white",
        }}
      >
        {/* Breadcrumbs */}
        <Box sx={{ backgroundColor: "#E8EDF6", py: 2 }}>
          <Container maxWidth="lg">
            <Breadcrumbs
              separator={<NavigateNext fontSize="small" />}
              aria-label="breadcrumb"
              sx={{ fontSize: "14px" }}
            >
              <Link
                color="inherit"
                href="/"
                sx={{
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {t("learnAbout.breadcrumbs.home")}
              </Link>
              {/* <Link
                color="inherit"
                href="#"
                sx={{
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {t("learnAbout.breadcrumbs.visas")}
              </Link> */}
              <Typography color="text.primary" sx={{ fontWeight: 500, fontSize: "14px" }}>
                {t("learnAbout.breadcrumbs.eta")}
              </Typography>
            </Breadcrumbs>
          </Container>
        </Box>

        {/* Header avec titre et bouton */}
        <Box sx={{ backgroundColor: "#E8EDF6", py: { xs: 3, md: 4 } }}>
          <Container maxWidth="lg">
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 600,
                  color: "#0C3058",
                  fontSize: { xs: "20px", md: "24px" },
                }}
              >
                {t("learnAbout.header.title")}
              </Typography>
              {/* Bouton visible seulement sur desktop */}
              {!isMobile && (
                <Button
                component={Link}
                  variant="contained"
                  size="large"
                  type="link"
                  sx={{
                    backgroundColor: "#0C3058",
                    color: "white",
                    px: 4,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      color:"#fff",
                      backgroundColor: "#0C3058",
                    },
                  }}
                  onClick={() => navigate("/apply-for-an-eta-il-1")}
                >
                  {t("learnAbout.header.applyButton")}
                </Button>
              )}
            </Box>
          </Container>
        </Box>
      </Box>

      {/* Bouton sticky mobile en bas de l'écran */}
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            bottom: 0,
            width: "100vw",
            zIndex: 200,
            backgroundColor: "#fff",
            boxShadow: "0 -2px 12px rgba(0,0,0,0.08)",
            p: 2,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
          component={Link}
            variant="contained"
            size="large"
            sx={{
              backgroundColor: "#0C3058",
              color: "white",
              px: 4,
              py: 1.5,
              textTransform: "none",
              fontWeight: 600,
              width: "100%",
              maxWidth: 420,
              boxShadow: 2,
              borderRadius: 2,
              "&:hover": {
                backgroundColor: "#083046",
              },
            }}
            onClick={() => navigate("/apply-for-an-eta-il-1")}
          >
            {t("learnAbout.header.applyButton")}
          </Button>
        </Box>
      )}

      {/* Onglets */}
      <Container maxWidth="lg" sx={{ mt: 4 }} ref={tabsRef}>
        <Box>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="ETA-IL tabs"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                minWidth: 120,
                px: 3,
                py: 2,
                // border: "1px solid #ddd",
                borderBottom: "none",
                marginRight: 1,
                borderRadius: "8px 8px 0 0",
                backgroundColor: "#f5f5f5",
                color: "#666",
                "&.Mui-selected": {
                  backgroundColor: "white",
                  color: "#0C3058",
                  borderColor: "#ddd",
                  borderBottomColor: "white",
                  zIndex: 1,
                  position: "relative",
                },
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            <Tab label={t("learnAbout.tabs.about")} />
            <Tab label={t("learnAbout.tabs.stepByStep")} />
          </Tabs>
        </Box>

        {/* Contenu de l'onglet About ETA-IL */}
        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={4}>
            {/* Section principale avec illustration */}
            <Grid>
              <Card
                sx={{
                  p: 0,
                  mb: 3,
                  backgroundColor: "",
                  borderRadius: " 8px",
                  // border: 0,
                  // boxShadow: 0,
                  // "&:hover": { boxShadow: 0 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 3,
                    flexDirection: { xs: "column-reverse", md: "row" },
                    
                  }}
                >
                  <Box sx={{ flex: 1.5, p: 2 }}>
                    <Typography
                      variant="h6"
                      component="h2"
                      sx={{
                        mb: 2,
                      
                      }}
                    >
                      {t("learnAbout.about.mainCard.title")}
                    </Typography>
                    
                    <Typography variant="body1" >
                      {t("learnAbout.about.mainCard.text")}
                    
                    </Typography>
                    <Typography variant="body1" >
                      <Link
                        onClick={() => navigate("/get-help")}
                        sx={{
                          color: "#0066CC",
                          textDecoration: "none",
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {t("learnAbout.about.mainCard.questionsLink")}
                      </Link>
                    </Typography>
                    
                  </Box>
                  <Box
                    sx={{
                      p: 2,
                      minWidth: 120,
                      flex: { xs: "1", md: "1" },
                      background:
                        "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                    }}
                  >
                    <img
                      src={`${prefix}/svg/TravelApproval.svg`}
                      alt=""
                      style={{ width: "100%", height: "auto" }}
                    />
                  </Box>
                </Box>
              </Card>

              {/* Informations de validité, durée, coût */}
              <Grid
                container
                spacing={10}
                sx={{ mb: 4, alignItems: "stretch", mt: 4 }}
              >
                <Grid
               
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <InfoStatCard
                    title={t("learnAbout.about.stats.validity.title")}
                    mainValue={t("learnAbout.about.stats.validity.mainValue")}
                    unit={t("learnAbout.about.stats.validity.unit")}
                    secondaryText={t(
                      "learnAbout.about.stats.validity.secondaryText"
                    )}
                    tooltipText={t("learnAbout.about.stats.validity.tooltip")}
                  />
                </Grid>
                {/* Ligne verticale */}
                <Grid
                  // item
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 1,
                      height: 100,
                      borderRight: "1px solid #B0BEC5",
                      mx: 0.5,
                    }}
                  />
                </Grid>
                <Grid
                  // item
                  
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <InfoStatCard
                    title={t("learnAbout.about.stats.duration.title")}
                    mainValue={`${t(
                      "learnAbout.about.stats.duration.mainValuePrefix"
                    )} ${t("learnAbout.about.stats.duration.mainValue")}`}
                    unit={t("learnAbout.about.stats.duration.unit")}
                    secondaryText={t(
                      "learnAbout.about.stats.duration.secondaryText"
                    )}
                    tooltipText={t("learnAbout.about.stats.duration.tooltip")}
                  />
                </Grid>
                {/* Ligne verticale */}
                  <Grid
                    
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 1,
                      height: 100,
                      borderRight: "1px solid #B0BEC5",
                      mx: 0.5,
                    }}
                  />
                </Grid>
                <Grid
              
                    
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <InfoStatCard
                    title={t("learnAbout.about.stats.cost.title")}
                    mainValue={t("learnAbout.about.stats.cost.mainValue")}
                    unit={t("learnAbout.about.stats.cost.unit")}
                    secondaryText={
                      <>
                        {t("learnAbout.about.stats.cost.secondaryTextLine1")}
                        <br />
                        <Typography variant="caption">
                          {t("learnAbout.about.stats.cost.secondaryTextLine2")}
                        </Typography>
                      </>
                    }
                    tooltipText={t("learnAbout.about.stats.cost.tooltip")}
                  />
                </Grid>
              </Grid>

              {/* Informations détaillées */}
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                {t("learnAbout.about.detailedInfo")}
              </Typography>

              {/* Sections What can you do / You must */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 4,
                  mb: 4,
                }}
              >
                <InfoListCard
                  title={t("learnAbout.about.whatCanYouDo.title")}
                  items={[
                    t("learnAbout.about.whatCanYouDo.item1"),
                    t("learnAbout.about.whatCanYouDo.item2"),
                    t("learnAbout.about.whatCanYouDo.item3"),
                  ]}
                />
                <InfoListCard
                  title={t("learnAbout.about.youMust.title")}
                  items={[
                    <>
                      {t("learnAbout.about.youMust.item1")} (
                      <Link
                        href={process.env.REACT_APP_SITE+"eligibilitynational"}
                        sx={{
                          color: "#0066CC",
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {t("learnAbout.about.youMust.item1_link")}
                      </Link>
                      )
                    </>,
                    <>
                      {t("learnAbout.about.youMust.item2")}{" "}
                      <Link
                      target="_blank"
                      aria-label={t("common.externalGovLink")}
                        href="https://www.gov.il/en/departments/dynamiccollectors/ashrotzarim?skip=0"
                        sx={{
                          color: "#0066CC",
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {t("learnAbout.about.youMust.item2_link")}
                      </Link>
                    </>,
                    t("learnAbout.about.youMust.item3"),
                  ]}
                />
              </Box>

              {/* Exemptions */}
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "#0C3058", mb: 2 }}
              >
                {t("learnAbout.about.exemptions.mainTitle")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 4,
                  mb: 4,
                }}
              >
                <InfoListCard
                  sections={[
                    {
                      title: t("learnAbout.about.exemptions.section1.title"),
                      items: [
                        t("learnAbout.about.exemptions.section1.item1"),
                        t("learnAbout.about.exemptions.section1.item2"),
                      ],
                    },
                    {
                      title: t("learnAbout.about.exemptions.section2.title"),
                      items: [
                        t("learnAbout.about.exemptions.section2.item1"),
                        t("learnAbout.about.exemptions.section2.item2"),
                        t("learnAbout.about.exemptions.section2.item3"),
                        t("learnAbout.about.exemptions.section2.item4"),
                      ],
                    },
                  ]}
                />
              </Box>

              {/* Nouvelle section (You can apply...) */}
              <Box sx={{ mt: 5 }}>
                <NumberedList
                  title={t("learnAbout.about.applyIf.title")}
                  items={[
                    t("learnAbout.about.applyIf.item1"),
                    t("learnAbout.about.applyIf.item2"),
                    t("learnAbout.about.applyIf.item3"),
                  ]}
                />

                <Typography
                  variant="body1"
                  sx={{ mb: 2, lineHeight: 1.6, fontWeight: "bold" }}
                >
                  {t("learnAbout.about.guarantee.title")}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                  {t("learnAbout.about.guarantee.text")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 4, fontStyle: "italic" }}
                >
                  {t("learnAbout.about.guarantee.note")}
                </Typography>

                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#0C3058", mb: 2 }}
                >
                  {t("learnAbout.about.video.title")}
                </Typography>

                <YoutubeCard
                  width={600}
                  height={340}
                  url="https://www.youtube.com/embed/r6yBwHYwo2A"
                />
                <Space size="medium" />
                <Button
                  variant="contained"
                  size="large"
                  type="link"
                  sx={{
                    backgroundColor: "#0C3058",
                    color: "white",
                    px: 4,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#083046",
                    },
                  }}
                  onClick={() => {
                    setCurrentTab(1);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  {t("learnAbout.about.video.button")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Contenu de l'onglet Step by step */}
        <TabPanel value={currentTab} index={1}>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 600,
              color: "#0C3058",
              mb: 4,
            }}
          >
            {t("learnAbout.stepByStep.mainTitle")}
          </Typography>

          {/* Étapes */}
          <Box sx={{ position: "relative" }}>
            {/* Ligne verticale */}
            <Box
              sx={{
                position: "absolute",
                left: 16,
                top: 40,
                bottom: 0,
                width: 2,
                backgroundColor: "#FF6B35",
                zIndex: 0,
              }}
            />

            {/* Étape 1 */}
            <Box
              sx={{ display: "flex", mb: 4, position: "relative", zIndex: 1 }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: "#FF6B35",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  mr: 3,
                  flexShrink: 0,
                }}
              >
                1
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h5"
                  component="h3"
                  sx={{ fontWeight: 600, color: "#0C3058", mb: 2 }}
                >
                  {t("learnAbout.stepByStep.step1.title")}
                </Typography>
                <List>
                  <ListItem sx={{ px: 0, py: 0.5 }}>
                    <ListItemText
                      primary={t("learnAbout.stepByStep.step1.item1")}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 0.5 }}>
                    <ListItemText
                      primary={t("learnAbout.stepByStep.step1.item2")}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 0.5 }}>
                    <ListItemText
                      primary={t("learnAbout.stepByStep.step1.item3")}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 0.5 }}>
                    <ListItemText
                      primary={t("learnAbout.stepByStep.step1.item4")}
                    />
                  </ListItem>
                </List>
              </Box>
            </Box>

            {/* Étape 2 */}
            <Box
              sx={{ display: "flex", mb: 4, position: "relative", zIndex: 1 }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: "#FF6B35",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  mr: 3,
                  flexShrink: 0,
                }}
              >
                2
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h5"
                  component="h4"
                  sx={{ fontWeight: 600, color: "#0C3058", mb: 2 }}
                >
                  {t("learnAbout.stepByStep.step2.title")}
                </Typography>
                <List>
                  <ListItem sx={{ px: 0, py: 0.5 }}>
                    <ListItemText
                      primary={t("learnAbout.stepByStep.step2.item1")}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 0.5 }}>
                    <ListItemText
                      primary={t("learnAbout.stepByStep.step2.item2")}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 0.5 }}>
                    <ListItemText
                      primary={t("learnAbout.stepByStep.step2.item3")}
                    />
                  </ListItem>
                </List>
              </Box>
            </Box>

            {/* Étape 3 */}
            <Box
              sx={{ display: "flex", mb: 4, position: "relative", zIndex: 1 }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: "#FF6B35",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  mr: 3,
                  flexShrink: 0,
                }}
              >
                3
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h5"
                  component="h5"
                  sx={{ fontWeight: 600, color: "#0C3058", mb: 2 }}
                >
                  {t("learnAbout.stepByStep.step3.title")}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                  {t("learnAbout.stepByStep.step3.text")}
                </Typography>
              </Box>
            </Box>

            {/* Étape 4 */}
            <Box
              sx={{ display: "flex", mb: 4, position: "relative", zIndex: 1 }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: "#FF6B35",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  mr: 3,
                  flexShrink: 0,
                }}
              >
                4
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h5"
                  component="h6"
                  sx={{ fontWeight: 600, color: "#0C3058", mb: 2 }}
                >
                  {t("learnAbout.stepByStep.step4.title")}
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#0C3058", mb: 1 }}
                  >
                    {t("learnAbout.stepByStep.step4.approved.title")}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                    {t("learnAbout.stepByStep.step4.approved.text")}
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#0C3058", mb: 1 }}
                  >
                    {t("learnAbout.stepByStep.step4.followUp.title")}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                    {t("learnAbout.stepByStep.step4.followUp.text")}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#0C3058", mb: 1 }}
                  >
                    {t("learnAbout.stepByStep.step4.denied.title")}
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                    {t("learnAbout.stepByStep.step4.denied.text")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: "italic", color: "#666", mt: 1 }}
                  >
                    {t("learnAbout.stepByStep.step4.denied.note")}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </TabPanel>
      </Container>

      <Space size="medium" />
    </>
  );
};

export default LearnAbout;
